import React, { useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Search, X } from "lucide-react";
import {
  STUDIO_ACTIONS,
  applyModuleFlags,
  displayRoleName,
  emptyModuleMatrix,
  enabledPermissionCount,
  moduleFlags,
  moduleTitle,
  orderedModuleKeys,
  permissionSummary,
  roleTone,
} from "./iamStudio";

const GUIDE_KEY = "iam_roles_guide_dismissed";

function Badge({ children, tone }) {
  return <span className={`iam-studio-badge ${tone}`}>{children}</span>;
}

export default function IamRolesStudio({ roles, catalog, loading, saving, onSaveRole }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [showGuide, setShowGuide] = useState(() => localStorage.getItem(GUIDE_KEY) !== "1");

  const modules = orderedModuleKeys(catalog);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return roles.filter((r) => {
      if (statusFilter && (r.status || "active") !== statusFilter) return false;
      if (!q) return true;
      return [r.name, displayRoleName(r.name), r.description, r.role_category]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [roles, search, statusFilter]);

  useEffect(() => {
    if (!editing) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setEditing(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing]);

  const openNew = () => {
    setError("");
    setEditing({
      roleId: null,
      name: "",
      description: "",
      status: "active",
      role_category: "custom",
      permission_matrix: catalog ? emptyModuleMatrix(catalog) : {},
    });
  };

  const openRole = (role) => {
    setError("");
    setEditing({
      roleId: role.role_id,
      name: role.name || "",
      description: role.description || "",
      status: role.status || "active",
      role_category: role.role_category || "custom",
      permission_matrix: role.permission_matrix || (catalog ? emptyModuleMatrix(catalog) : {}),
    });
  };

  const toggle = (moduleKey, action, checked) => {
    setEditing((current) => {
      const flags = moduleFlags(current.permission_matrix, moduleKey, catalog.modules[moduleKey]);
      flags[action] = checked;
      return {
        ...current,
        permission_matrix: applyModuleFlags(
          current.permission_matrix,
          moduleKey,
          catalog.modules[moduleKey],
          flags
        ),
      };
    });
  };

  const toggleRow = (moduleKey, checked) => {
    setEditing((current) => {
      const flags = {};
      STUDIO_ACTIONS.forEach(({ key }) => {
        flags[key] = checked;
      });
      return {
        ...current,
        permission_matrix: applyModuleFlags(
          current.permission_matrix,
          moduleKey,
          catalog.modules[moduleKey],
          flags
        ),
      };
    });
  };

  const save = async () => {
    if (!editing.name.trim()) {
      setError("Role name is required.");
      return;
    }
    setError("");
    try {
      await onSaveRole(editing.roleId, {
        name: editing.name.trim(),
        description: editing.description,
        status: editing.status,
        role_category: editing.role_category || "custom",
        permission_matrix: editing.permission_matrix,
      });
      setEditing(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Could not save the role");
    }
  };

  const dismissGuide = () => {
    localStorage.setItem(GUIDE_KEY, "1");
    setShowGuide(false);
  };

  const filtersOn = Boolean(search || statusFilter);

  return (
    <div className={`iam-ux ${editing ? "with-drawer" : ""}`}>
      <div className="iam-ux-main">
        <header className="iam-ux-head">
          <div>
            <h2>Roles</h2>
            <p>A role is what a person can do. Locations and departments stay on Access Scope.</p>
          </div>
          <span className="iam-ux-count">{filtered.length} {filtered.length === 1 ? "role" : "roles"}</span>
        </header>

        {showGuide && (
          <div className="iam-ux-guide">
            <article>
              <span>1</span>
              <div>
                <strong>Name the job</strong>
                <p>HR, Manager, Payroll — a short name people will recognise.</p>
              </div>
            </article>
            <article>
              <span>2</span>
              <div>
                <strong>Tick the modules</strong>
                <p>View, add, edit, delete, approve, and export. One row per module.</p>
              </div>
            </article>
            <article>
              <span>3</span>
              <div>
                <strong>Assign it on Users</strong>
                <p>People inherit these permissions. You do not set them again per user.</p>
              </div>
            </article>
            <button type="button" onClick={dismissGuide}>Got it</button>
          </div>
        )}

        <div className="iam-ux-toolbar">
          <label className="iam-ux-search">
            <Search size={16} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search roles" />
          </label>
          <select className="iam-ux-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Status: All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {filtersOn && (
            <button type="button" className="iam-ux-clear" onClick={() => { setSearch(""); setStatusFilter(""); }}>
              Clear filters
            </button>
          )}
          <button type="button" className="iam-ux-add" onClick={openNew}>
            <Plus size={16} /> Create Role
          </button>
        </div>

        <div className="iam-ux-card">
          {loading || !catalog ? <p className="iam-ux-empty">Loading roles…</p> : (
            <table className="iam-ux-table">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Description</th>
                  <th>Permission summary</th>
                  <th>Users</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((role) => {
                  const count = enabledPermissionCount(role.permission_matrix, catalog);
                  const summary = permissionSummary(role.permission_matrix, catalog);
                  const detail = summary.includes("·") ? summary.split("·").slice(1).join("·").trim() : (count ? "Limited modules" : "Nothing enabled yet");
                  const label = displayRoleName(role.name);
                  return (
                    <tr key={role.role_id} onClick={() => openRole(role)}>
                      <td>
                        <div className="iam-studio-person">
                          <span className={`iam-studio-avatar ${roleTone(role.name)}`}>{label[0] || "R"}</span>
                          <span>
                            <strong>{label}</strong>
                            <small>{role.role_category || "custom"}</small>
                          </span>
                        </div>
                      </td>
                      <td className="iam-ux-desc">{role.description || "No description yet"}</td>
                      <td>
                        <div className={`iam-ux-perms ${count ? "on" : ""}`}>
                          <strong>{count} {count === 1 ? "permission" : "permissions"}</strong>
                          <small>{detail}</small>
                        </div>
                      </td>
                      <td>
                        <span className="iam-ux-users">{role.assigned_user_count || 0}</span>
                      </td>
                      <td>
                        <Badge tone={role.status === "inactive" ? "inactive" : "active"}>
                          {role.status === "inactive" ? "Inactive" : "Active"}
                        </Badge>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="iam-ux-edit"
                          onClick={(event) => {
                            event.stopPropagation();
                            openRole(role);
                          }}
                        >
                          <Pencil size={14} /> Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {!loading && catalog && !roles.length && (
            <div className="iam-ux-zero">
              <strong>No roles yet</strong>
              <p>Create a role, tick the modules that job needs, then assign it from Users.</p>
              <button type="button" className="iam-ux-add" onClick={openNew}><Plus size={16} /> Create Role</button>
            </div>
          )}
          {!loading && catalog && roles.length > 0 && !filtered.length && (
            <p className="iam-ux-empty">No roles match this search.</p>
          )}
        </div>
      </div>

      {editing && <button type="button" className="iam-ux-backdrop" aria-label="Close panel" onClick={() => setEditing(null)} />}

      {editing && catalog && (
        <aside className="iam-ux-drawer wide">
          <header>
            <div>
              <h3>{editing.roleId ? `Edit Role — ${displayRoleName(editing.name) || "Role"}` : "Create Role"}</h3>
              <p>Tick what this role can do in each module.</p>
            </div>
            <button type="button" className="iam-studio-icon" onClick={() => setEditing(null)} aria-label="Close">
              <X size={16} />
            </button>
          </header>
          <div className="iam-ux-drawer-body">
            <section className="iam-ux-section">
              <h4>Role information</h4>
              <label className="iam-studio-field">
                Role name
                <input value={editing.name} onChange={(e) => setEditing((d) => ({ ...d, name: e.target.value }))} placeholder="Manager" />
              </label>
              <label className="iam-studio-field">
                Description
                <textarea
                  rows={2}
                  value={editing.description}
                  placeholder="Who this role is for"
                  onChange={(e) => setEditing((d) => ({ ...d, description: e.target.value }))}
                />
              </label>
              <label className="iam-studio-field">
                Status
                <select value={editing.status} onChange={(e) => setEditing((d) => ({ ...d, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>
            </section>

            <section className="iam-ux-section">
              <h4>Module permissions</h4>
              <p className="iam-ux-note">Use the first box to turn a whole module on or off.</p>
              <div className="iam-studio-matrix-wrap">
                <table className="iam-studio-matrix iam-ux-matrix">
                  <thead>
                    <tr>
                      <th>Module</th>
                      {STUDIO_ACTIONS.map((action) => <th key={action.key}>{action.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map((moduleKey) => {
                      const flags = moduleFlags(editing.permission_matrix, moduleKey, catalog.modules[moduleKey]);
                      const allOn = STUDIO_ACTIONS.every((action) => flags[action.key]);
                      return (
                        <tr key={moduleKey}>
                          <td>
                            <label className="iam-ux-mod">
                              <input
                                type="checkbox"
                                aria-label={`All ${moduleTitle(moduleKey)} permissions`}
                                checked={allOn}
                                onChange={(e) => toggleRow(moduleKey, e.target.checked)}
                              />
                              {moduleTitle(moduleKey)}
                            </label>
                          </td>
                          {STUDIO_ACTIONS.map((action) => (
                            <td key={action.key}>
                              <input
                                type="checkbox"
                                aria-label={`${moduleTitle(moduleKey)} ${action.label}`}
                                checked={!!flags[action.key]}
                                onChange={(e) => toggle(moduleKey, action.key, e.target.checked)}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
            {error && <p className="iam-studio-error">{error}</p>}
          </div>
          <footer>
            <button type="button" className="iam-studio-btn" onClick={() => setEditing(null)}>Cancel</button>
            <button type="button" className="iam-studio-btn primary" disabled={saving} onClick={save}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </footer>
        </aside>
      )}
    </div>
  );
}
